import {
  Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ElementRef,
  Input,
  forwardRef,
  ViewEncapsulation,
  Optional,
  Attribute,
  SimpleChanges,
  OnChanges,
  NgZone,
  Inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { Directionality } from '@angular/cdk/bidi';
import { MatSlider } from '@angular/material/slider';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import { ThemePalette } from '@angular/material/core';

export const SG_INPUT_SLIDER_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SgInputSliderComponent),
  multi: true,
};

@Component({
  selector: 'sg-input-slider',
  exportAs: 'matSlider',
  providers: [SG_INPUT_SLIDER_VALUE_ACCESSOR],
  host: {
    '(focus)': '_onFocus()',
    '(blur)': '_onBlur()',
    '(mousedown)': '_onMousedown($event)',
    '(keydown)': '_onKeydown($event)',
    '(keyup)': '_onKeyup()',
    '(mouseenter)': '_onMouseenter()',
    '(slide)': '_onSlide($event)',
    '(slideend)': '_onSlideEnd()',
    '(slidestart)': '_onSlideStart($event)',
    class: 'mat-slider sg-input-slider',
    role: 'slider',
    '[tabIndex]': 'tabIndex',
    '[attr.aria-disabled]': 'disabled',
    '[attr.aria-valuemax]': 'max',
    '[attr.aria-valuemin]': 'min',
    '[attr.aria-valuenow]': 'value',
    '[attr.aria-orientation]': 'vertical ? "vertical" : "horizontal"',
    '[class.mat-slider-disabled]': 'disabled',
    '[class.mat-slider-has-ticks]': 'tickInterval',
    '[class.mat-slider-horizontal]': '!vertical',
    '[class.mat-slider-axis-inverted]': '_invertAxis',
    '[class.mat-slider-sliding]': '_isSliding',
    '[class.mat-slider-thumb-label-showing]': 'thumbLabel',
    '[class.mat-slider-vertical]': 'vertical',
    '[class.mat-slider-min-value]': '_isMinValue',
    '[class.mat-slider-hide-last-tick]': 'disabled || _isMinValue && _thumbGap && _invertAxis',
  },
  templateUrl: './input-slider.component.html',
  styleUrls: ['./input-slider.component.scss'],
  inputs: ['disabled', 'color', 'tabIndex'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SgInputSliderComponent implements OnChanges {
  private _value: number = 0;
  private sgRoundLabelTo: number = 0;

  @Input()
  get thumbLabel(): boolean {
    return false;
  }
  set thumbLabel(value: boolean) {}

  @Input() get value(): number {
    return this._value;
  }
  set value(val: number) {
    this._value = val;
  }

  @Input() min: number = 0;
  @Input() max: number = 100;
  @Input() step: number = 1;
  @Input() disabled: boolean = false;
  @Input() color: ThemePalette;
  @Input() tabIndex: number = 0;
  @Input() vertical: boolean = false;
  @Input() tickInterval: number | 'auto' = 0;

  // Properties needed for template
  get _invertAxis(): boolean {
    return false;
  }
  get _isSliding(): boolean {
    return false;
  }
  get _isMinValue(): boolean {
    return this.value === this.min;
  }
  get _thumbGap(): number {
    return 0;
  }

  get sgDisplayValue(): string | number {
    if (this.sgRoundLabelTo && this.value && this.value % 1 !== 0) {
      return this.value.toFixed(this.sgRoundLabelTo);
    }
    return this.value || 0;
  }

  constructor(
    private eRef: ElementRef,
    private focusMonitor: FocusMonitor,
    private cdRef: ChangeDetectorRef,
    @Optional() private dir: Directionality,
    @Attribute('tabindex') tabIndex: string,
    private ngZone: NgZone,
    @Optional() @Inject(DOCUMENT) private document: any
  ) {
    this.tabIndex = parseInt(tabIndex) || 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('step' in changes) {
      if (this.step % 1 !== 0) {
        this.sgRoundLabelTo = this.step.toString().split('.').pop()!.length;
      }
    }
  }

  inputValueChanged(value: string): void {
    this.value = Number(value);
  }

  // Event handlers required by host bindings
  _onFocus(): void {}
  _onBlur(): void {}
  _onMousedown(event: MouseEvent): void {}
  _onKeydown(event: KeyboardEvent): void {}
  _onKeyup(): void {}
  _onMouseenter(): void {}
  _onSlide(event: any): void {}
  _onSlideEnd(): void {}
  _onSlideStart(event: any): void {}

  // Style methods required by template
  _getTrackBackgroundStyles(): any {
    return {};
  }

  _getTrackFillStyles(): any {
    const percentage = ((this.value - this.min) / (this.max - this.min)) * 100;
    return {
      transform: `translateX(-${100 - percentage}%) scaleX(${percentage / 100})`,
    };
  }

  _getTicksContainerStyles(): any {
    return {};
  }

  _getTicksStyles(): any {
    return {};
  }

  _getThumbContainerStyles(): any {
    const percentage = ((this.value - this.min) / (this.max - this.min)) * 100;
    return {
      transform: `translateX(-50%) translateX(${percentage}%)`,
    };
  }
}
